import type { PaymentMethod, PaymentRecord } from "./types";

export const HST_RATE_BPS = 1300;
export const calculateHstCents = (subtotalCents: number) =>
  Math.round((subtotalCents * HST_RATE_BPS) / 10_000);

export function paymentQuote(payment: PaymentRecord) {
  const amount = payment.amountCents ?? Math.round((payment.amount ?? 0) * 100);
  const pricing = payment.settlementPricing;
  const applied =
    payment.details?.type === "team-fee" && payment.details.hstAdded
      ? payment.details
      : undefined;
  const fixed = Boolean(
    pricing ||
    applied ||
    (payment.paidCents ?? 0) > 0 ||
    payment.status === "paid" ||
    payment.status === "waived",
  );
  const baseCents =
    pricing?.baseCents ?? applied?.taxableSubtotalCents ?? amount;
  const hstCents =
    pricing?.hstCents ??
    (applied
      ? (applied.hstCents ?? 0)
      : !fixed && !payment.cashOnly && payment.electronicHst !== false
        ? calculateHstCents(baseCents)
        : 0);
  return {
    baseCents,
    hstCents,
    cashCents: fixed ? amount : baseCents,
    electronicCents: fixed ? amount : baseCents + hstCents,
    fixed,
    conditional: !fixed && hstCents > 0,
  };
}

export function paymentTotalForMethod(
  payment: PaymentRecord,
  method: PaymentMethod,
) {
  const quote = paymentQuote(payment);
  return method === "cash" || payment.cashOnly
    ? quote.cashCents
    : quote.electronicCents;
}

/** Name and address search with exact, prefix, substring, then fuzzy matches. */
export function payeeSearchScore(
  query: string,
  name: string,
  email: string,
): number {
  const normalize = (value: string) =>
    value
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .trim();
  const words = normalize(query).split(/\s+/).filter(Boolean);
  if (!words.length) return 0;
  const fields = [normalize(name), normalize(email)];
  let score = 0;
  for (const word of words) {
    const matches = fields.map((field) => {
      if (field === word) return 0;
      if (field.startsWith(word)) return 1;
      if (field.includes(word)) return 2;
      let index = 0;
      for (const char of field) if (char === word[index]) index++;
      return index === word.length ? 3 : Infinity;
    });
    score += Math.min(...matches);
  }
  return score;
}

export function withClassPass(
  payment: PaymentRecord,
  classPassCents: number | null,
): PaymentRecord {
  if (!(
    payment.category === "team-fee" ||
    (!payment.category && payment.kind === "monthly")
  ))
    throw new Error("Class passes can only be added to Team Fees");
  if (paymentQuote(payment).fixed)
    throw new Error(
      "Recorded payment pricing is fixed. Create a separate adjustment.",
    );
  if (
    classPassCents !== null &&
    (!Number.isSafeInteger(classPassCents) || classPassCents < 0)
  )
    throw new Error("Enter a valid class pass amount");
  const details =
    payment.details?.type === "team-fee" ? payment.details : undefined;
  const oldPass = details?.classPassAdded ? (details.classPassCents ?? 0) : 0;
  const baseFeeCents =
    (payment.amountCents ?? Math.round((payment.amount ?? 0) * 100)) - oldPass;
  if (baseFeeCents < 0)
    throw new Error("Class pass exceeds the recorded subtotal");
  const amountCents = baseFeeCents + (classPassCents ?? 0);
  return {
    ...payment,
    category: "team-fee",
    amount: amountCents / 100,
    amountCents,
    details: {
      type: "team-fee",
      month: payment.month ?? details?.month ?? "",
      baseFeeCents,
      classPassAdded: classPassCents !== null,
      classPassCents: classPassCents ?? undefined,
      hstAdded: false,
    },
  };
}
